import { useCallback, useMemo } from 'react';
import FilterDropdown from 'components/elements/Dropdown/FilterDropdown';
import { useDashboardVerticals, useDashboardVerticalsExclusions } from 'state/dashboard';
import { useSectors } from 'state/projectTypes';
import { DEFAULT_VERTICAL_EXCLUSIONS } from 'utils/constants';

const SectorsFilter = () => {
  const [savedVerticals, setVerticals] = useDashboardVerticals();
  const [verticalsExclusions, setVerticalsExclusions] = useDashboardVerticalsExclusions();
  const {
    verticals: allVerticals,
    sectors: allSectors,
    sectorsLoading,
    verticalsLoading,
  } = useSectors();

  // Sectors Dropdown
  const sectorOptions = useMemo(() => {
    return allSectors
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(({ name }) => name !== '')
      .map(({ name: sectorName }) => {
        return {
          label: sectorName,
          value: sectorName,
          children: allVerticals
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter(({ name, sector }) => name !== '' && sector.name === sectorName)
            .map(({ name }) => {
              return { label: name, value: name };
            }),
        };
      });
  }, [allSectors, allVerticals, sectorsLoading || verticalsLoading]);

  const handleSelectionTransformation = useCallback(
    (values: string[], isParent: boolean, currentSelection: number[]): number[] => {
      const currentSet = new Set(currentSelection);

      if (isParent) {
        allVerticals.forEach((vertical) => {
          const relatedVerticalIds = allVerticals
            .filter((v) => v.sector.name === vertical.sector.name)
            .map((v) => v.id);

          if (values.includes(vertical.sector.name)) {
            const isAlreadySelected = relatedVerticalIds.some((id) => currentSet.has(id));

            if (!isAlreadySelected) {
              // If not already partially selected, add all related verticals
              relatedVerticalIds.forEach((id) => currentSet.add(id));
            }
            // If it's already partially selected, do nothing (keep the current selections)
          } else {
            relatedVerticalIds.forEach((id) => {
              if (!values.includes(vertical.name)) {
                currentSet.delete(id);
              }
            });
          }
        });
      } else {
        // For child verticals
        allVerticals.forEach((vertical) => {
          if (values.includes(vertical.name)) {
            currentSet.add(vertical.id);
          } else {
            currentSet.delete(vertical.id);
          }
        });
      }

      return Array.from(currentSet);
    },
    [allVerticals],
  );

  const sectorsSubmitted = useCallback(
    (values: string[], isParent: boolean) => {
      const updatedSelection = handleSelectionTransformation(
        values,
        isParent,
        savedVerticals,
      );
      setVerticals(Array.from(updatedSelection));
    },
    [savedVerticals],
  );

  const verticalExclusionsSubmitted = useCallback(
    (values: string[], isParent: boolean) => {
      const updatedSelection = handleSelectionTransformation(
        values,
        isParent,
        verticalsExclusions,
      );
      setVerticalsExclusions(Array.from(updatedSelection));
    },
    [verticalsExclusions],
  );

  const onSubmit = useCallback(
    (values: string[], isParent: boolean) => {
      sectorsSubmitted(values, isParent);
    },
    [sectorsSubmitted],
  );

  const onClearAll = useCallback(() => {
    setVerticals([]);
  }, [setVerticals]);

  const checkedValues = useMemo(() => {
    return savedVerticals.map(
      (id) => allVerticals.find((vertical) => vertical.id === id)?.sector.name || '',
    );
  }, [savedVerticals, allVerticals]);

  const childValues = useMemo(() => {
    return savedVerticals.map(
      (id) => allVerticals.find((vertical) => vertical.id === id)?.name || '',
    );
  }, [savedVerticals, allVerticals]);

  const exclusionValues = useMemo(() => {
    return verticalsExclusions.map(
      (id) => allVerticals.find((vertical) => vertical.id === id)?.sector.name || '',
    );
  }, [verticalExclusionsSubmitted, allVerticals]);

  const exclusionChildValues = useMemo(() => {
    return verticalsExclusions.map(
      (id) => allVerticals.find((vertical) => vertical.id === id)?.name || '',
    );
  }, [verticalsExclusions, allVerticals]);

  const onExclusionSubmit = useCallback(
    (values: string[], isParent: boolean) => {
      verticalExclusionsSubmitted(values, isParent);
    },
    [verticalExclusionsSubmitted],
  );

  return (
    <FilterDropdown
      options={sectorOptions}
      onSubmit={onSubmit}
      onClearAll={onClearAll}
      checkedValues={checkedValues}
      childValues={childValues}
      setCheckedValues={setVerticals}
      setChildValues={setVerticals}
      isExclusion
      exclusionValues={exclusionValues}
      exclusionChildValues={exclusionChildValues}
      onExclusionSubmit={onExclusionSubmit}
      setExclusionValues={setVerticalsExclusions}
      setChildExclusionValues={setVerticalsExclusions}
      onExclusionClearAll={() => setVerticalsExclusions(DEFAULT_VERTICAL_EXCLUSIONS)}
      description="A project’s proposed use case"
      loading={sectorsLoading || verticalsLoading}
    >
      Sectors
    </FilterDropdown>
  );
};

export default SectorsFilter;
